/**
 * Earthquake size: magnitude differences, energy, moment magnitude and Richter-style
 * local magnitude.
 *
 * Sources
 * - Hanks & Kanamori (1979), "A moment magnitude scale", J. Geophys. Res. 84(B5).
 *   Mw = (2/3) log10 M0 − 10.7 with M0 in dyne·cm, which is Mw = (2/3)(log10 M0 − 9.1)
 *   with M0 in N·m. The 9.1 constant is the one USGS and the IASPEI standard use (the
 *   exact conversion of 10.7 is 9.05).
 * - Gutenberg & Richter (1956): log10 E = 1.5 M + 4.8, E in joules. One magnitude unit
 *   is a factor of 10^1.5 ≈ 31.6 in energy and 10 in amplitude (USGS: "32 times more
 *   energy").
 * - USGS also gives log10 E = 5.24 + 1.44 Mw as a rough estimate from moment magnitude.
 * - Hutton & Boore (1987), BSSA 77(6): ML = log10 A + 1.110 log10(r/100)
 *   + 0.00189 (r − 100) + 3.0, A the peak Wood-Anderson amplitude in mm, r the
 *   hypocentral distance in km. At r = 100 km this reduces to Richter's original
 *   definition: 1 mm at 100 km is ML 3.0.
 */

export const ENERGY_PER_MAGNITUDE = 10 ** 1.5;
export const AMPLITUDE_PER_MAGNITUDE = 10;
/** Joules in one metric ton of TNT, by the thermochemical definition. */
export const TNT_JOULES_PER_TON = 4.184e9;
export const DYNE_CM_PER_NEWTON_METRE = 1e7;
/** Constant in Mw = (2/3)(log10 M0 − 9.1), M0 in N·m. */
export const MOMENT_CONSTANT = 9.1;
/** The S–P lag rule of thumb: kilometres of distance per second of S–P time. */
export const DEFAULT_KM_PER_SP_SECOND = 8;

/** Ground-motion amplitude ratio for a magnitude difference. */
export function amplitudeRatio(difference: number): number {
  return AMPLITUDE_PER_MAGNITUDE ** difference;
}

/** Energy ratio for a magnitude difference: 10^(1.5 Δ). */
export function energyRatio(difference: number): number {
  return 10 ** (1.5 * difference);
}

/** Radiated energy in joules by Gutenberg–Richter: log10 E = 1.5 M + 4.8. */
export function energyJoules(magnitude: number): number {
  return 10 ** (1.5 * magnitude + 4.8);
}

/** USGS's rough estimate from moment magnitude: log10 E = 5.24 + 1.44 Mw (joules). */
export function energyJoulesFromMw(mw: number): number {
  return 10 ** (5.24 + 1.44 * mw);
}

export function joulesToTntTons(joules: number): number {
  return joules / TNT_JOULES_PER_TON;
}

/** Moment magnitude from a seismic moment in N·m. Null for a non-positive moment. */
export function momentMagnitude(momentNm: number): number | null {
  if (!(momentNm > 0 && Number.isFinite(momentNm))) {
    return null;
  }
  return (2 / 3) * (Math.log10(momentNm) - MOMENT_CONSTANT);
}

/** Seismic moment in N·m for a moment magnitude. */
export function seismicMoment(mw: number): number {
  return 10 ** (1.5 * mw + MOMENT_CONSTANT);
}

/** M0 = μ · A · D, with μ in Pa, A in m² and D in m, giving N·m. */
export function momentFromFault(
  rigidityPa: number,
  areaM2: number,
  slipM: number
): number {
  return rigidityPa * areaM2 * slipM;
}

/** Epicentral-style distance from an S–P lag, at `kmPerSecond` km per second of lag. */
export function distanceFromSpLag(
  lagSeconds: number,
  kmPerSecond = DEFAULT_KM_PER_SP_SECOND
): number {
  return lagSeconds * kmPerSecond;
}

/** The distance correction −log10 A0(r) of Hutton & Boore (1987). */
export function distanceCorrection(distanceKm: number): number {
  return (
    1.11 * Math.log10(distanceKm / 100) + 0.001_89 * (distanceKm - 100) + 3
  );
}

export interface LocalMagnitudeResult {
  magnitude: number;
  /** log10 of the amplitude in mm. */
  logAmplitude: number;
  /** −log10 A0(r). */
  correction: number;
  /** The amplitude, in mm, that would be ML 0 at this distance. */
  zeroAmplitudeMm: number;
}

/** ML from a Wood-Anderson peak amplitude (mm) and a distance (km). Null if either is not positive. */
export function localMagnitude(
  amplitudeMm: number,
  distanceKm: number
): LocalMagnitudeResult | null {
  if (!(amplitudeMm > 0 && distanceKm > 0)) {
    return null;
  }
  const logAmplitude = Math.log10(amplitudeMm);
  const correction = distanceCorrection(distanceKm);
  return {
    magnitude: logAmplitude + correction,
    logAmplitude,
    correction,
    zeroAmplitudeMm: 10 ** -correction,
  };
}

/** The distance range Hutton & Boore's data covered. */
export const LOCAL_MAGNITUDE_RANGE_KM = { min: 10, max: 700 } as const;
